if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        this.sectionId = this.dataset.sectionId;

        /*
         * Map:
         * media ID -> variant
         *
         * Example:
         * {
         *   "123456789": {
         *      id: 987654321,
         *      available: true
         *   }
         * }
         */
        try {
          this.variantMediaMap = JSON.parse(
            this.dataset.variantMediaMap || '{}'
          );
        } catch (error) {
          console.error(
            'Unable to parse variant media map:',
            error
          );

          this.variantMediaMap = {};
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener(
              'click',
              this.onThumbnailClick.bind(
                this,
                mediaToSwitch.dataset.target
              )
            );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * Clicking a product image now does TWO things:
       *
       * 1. Switch gallery image
       * 2. Switch Shopify Variant
       */
      onThumbnailClick(mediaId, event) {
        event.preventDefault();

        this.setActiveMedia(mediaId, false);

        this.selectVariantFromMedia(mediaId);
      }

      /*
       * Convert:
       *
       * section-id-123456789
       *
       * into:
       *
       * 123456789
       */
      getMediaId(mediaTarget) {
        if (!mediaTarget) return null;

        const match = String(mediaTarget).match(/(\d+)$/);

        if (!match) return null;

        return match[1];
      }

      /*
       * Find Variant using selected image
       */
      selectVariantFromMedia(mediaTarget) {
        const mediaId = this.getMediaId(mediaTarget);

        if (!mediaId) return;

        const variant = this.variantMediaMap[mediaId];

        /*
         * Some gallery images are not Variant images.
         * In that case, just change image and keep
         * the current Variant.
         */
        if (!variant) {
          return;
        }

        this.updateVariantInput(variant);

        this.updateVariantPicker(variant);

        this.updateVariantUrl(variant);

        this.updateAddToCartButton(variant);

        this.updatePickupAvailability(variant);

        /*
         * Fire events so other theme scripts
         * can react to the Variant change.
         */
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              variantId: variant.id,
              mediaId: mediaId,
            },
          })
        );
      }

      /*
       * Most important part:
       *
       * Update input[name="id"]
       * used by Add to Cart.
       */
      updateVariantInput(variant) {
        const section =
          document.getElementById(
            `MainProduct-${this.sectionId}`
          ) ||
          document.getElementById(
            `ProductInfo-${this.sectionId}`
          ) ||
          this.closest('.product') ||
          document;

        const inputs = section.querySelectorAll(
          'form[action*="/cart/add"] input[name="id"], ' +
          'input.product-variant-id[name="id"]'
        );

        inputs.forEach((input) => {
          input.value = variant.id;
          input.disabled = false;

          input.dispatchEvent(
            new Event('input', {
              bubbles: true,
            })
          );

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      /*
       * Try to update Dawn's Variant picker too.
       *
       * This allows Dawn's existing product-info.js
       * logic to continue working when possible.
       */
      updateVariantPicker(variant) {
        const productInfo =
          document.getElementById(
            `ProductInfo-${this.sectionId}`
          ) ||
          this.closest('.product') ||
          document;

        /*
         * Newer Dawn versions may have
         * <variant-selects>
         * or <variant-radios>.
         */
        const picker =
          productInfo.querySelector('variant-selects') ||
          productInfo.querySelector('variant-radios') ||
          productInfo.querySelector(
            'product-form [data-variant-id]'
          );

        /*
         * We don't force option values here because
         * Variant option structure can differ between
         * products.
         *
         * Add to Cart is already correctly updated by
         * updateVariantInput().
         */
        if (!picker) return;

        picker.dataset.selectedVariantId =
          String(variant.id);
      }

      /*
       * Update URL:
       *
       * ?variant=123456
       */
      updateVariantUrl(variant) {
        try {
          const url = new URL(window.location.href);

          url.searchParams.set(
            'variant',
            variant.id
          );

          window.history.replaceState(
            {},
            '',
            url.toString()
          );
        } catch (error) {
          // Do nothing if URL cannot be updated.
        }
      }

      /*
       * Update Add to Cart availability.
       */
      updateAddToCartButton(variant) {
        const section =
          document.getElementById(
            `ProductInfo-${this.sectionId}`
          ) ||
          this.closest('.product') ||
          document;

        const submitButtons =
          section.querySelectorAll(
            'button[name="add"], .product-form__submit'
          );

        submitButtons.forEach((button) => {
          button.disabled = !variant.available;

          const text = button.querySelector('span');

          if (!text) return;

          if (variant.available) {
            /*
             * Preserve translated Add to cart text
             * whenever possible.
             */
            if (button.dataset.originalText) {
              text.textContent =
                button.dataset.originalText;
            } else {
              button.dataset.originalText =
                text.textContent.trim();
            }
          }
        });
      }

      /*
       * Update pickup availability Variant ID.
       */
      updatePickupAvailability(variant) {
        const pickupAvailability =
          document.querySelector(
            `#ProductInfo-${this.sectionId} pickup-availability`
          ) ||
          document.querySelector(
            `pickup-availability[data-section-id="${this.sectionId}"]`
          );

        if (!pickupAvailability) return;

        pickupAvailability.dataset.variantId =
          variant.id;
      }

      onSlideChanged(event) {
        const currentElement =
          event.detail.currentElement;

        if (!currentElement) return;

        const mediaTarget =
          currentElement.dataset.mediaId;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaTarget}"]`
          );

        this.setActiveThumbnail(thumbnail);

        /*
         * On mobile, swiping to another image
         * should also select its Variant.
         */
        this.selectVariantFromMedia(mediaTarget);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia?.classList?.add(
          'is-active'
        );

        if (prepend) {
          if (
            activeMedia.parentElement.firstChild !==
            activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          /*
           * Don't scroll page if image is already
           * visible.
           */
          if (activeMediaRect.top > -0.5) {
            return;
          }

          const top =
            activeMediaRect.top +
            window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          return;
        }

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(
          activeThumbnail
        );

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute(
              'aria-current'
            )
          );

        const button =
          thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            true
          );
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) {
          return;
        }

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}